import React from 'react';
import { ApolloError, ServerError as _ServerError, ServerParseError as _ServerParseError } from '@apollo/client';
import { environment } from '~/util/environment';
import { BEM } from '@feedfm/feed-util';

const bem = new BEM( 'ops-error-display', [] );

// Apollo doesn't seem to offer this as a type guard, so we'll make our own.
function isServerParseError( error: Error ): error is _ServerParseError {
  const serverParseError = error as _ServerParseError;

  return (
    serverParseError.response !== undefined &&
    typeof serverParseError.statusCode === 'number' &&
    typeof serverParseError.bodyText === 'string'
  );
}

// Ditto.
function isServerError( error: Error ): error is _ServerError {
  const serverError = error as _ServerError;

  return (
    serverError.response !== undefined &&
    typeof serverError.statusCode === 'number' &&
    typeof serverError.result === 'object'
  );
}

const ServerParseError = ( { error }: { error: _ServerParseError } ) => (
  <div className={ BEM.$( bem.el( 'server-parse-error' ) ) }>{ error.message }</div>
);

ServerParseError.displayName = 'ServerParseError';

const ServerError = ( { error }: { error: _ServerError } ) => (
  <div className={ BEM.$( bem.el( 'server-error' ) ) }>
    { typeof error.result === 'string' && error.result }
    { typeof error.result !== 'string' &&
      error.result.errors &&
      Array.isArray( error.result.errors ) &&
      error.result.errors.map( ( subError ) => subError.message ).join( ', ' ) }
  </div>
);

ServerError.displayName = 'ServerError';

const NetworkError = ( { error }: { error: ApolloError['networkError'] } ) =>
  error && (
    <div className={ BEM.$( bem.el( 'suberror' ), bem.el( 'network-error' ) ) }>
      <div className={ BEM.$( bem.el( 'suberror-heading' ) ) }>Network Error:</div>
      { isServerParseError( error ) && <ServerParseError error={ error } /> }
      { !isServerParseError( error ) && isServerError( error ) && <ServerError error={ error } /> }
    </div>
  );

NetworkError.displayName = 'NetworkError';

// This is a stub. I don't know if it's correct because the type is so vague.
const GraphQLErrorsList = ( { errors }: { errors: ApolloError['graphQLErrors'] } ) => (
  <>
    { errors.length > 0 && (
      <div className={ BEM.$( bem.el( 'suberror' ), bem.el( 'graphql-errors' ) ) }>
        <div className={ BEM.$( bem.el( 'suberror-heading' ) ) }>GraphQL Errors:</div>
        <ul>
          { errors.map( ( graphQLError, index ) => (
            <li key={ index }>{ graphQLError.message }</li>
          ) ) }
        </ul>
      </div>
    ) }
  </>
);

GraphQLErrorsList.displayName = 'GraphQLErrorsList';

const ClientErrorsList = ( { errors }: { errors: ApolloError['clientErrors'] } ) => (
  <>
    { errors.length > 0 && (
      <div className={ BEM.$( bem.el( 'suberror' ), bem.el( 'client-errors' ) ) }>
        <div className={ BEM.$( bem.el( 'suberror-heading' ) ) }>Client Errors:</div>
        <ul>
          { errors.map( ( clientError, index ) => (
            <li key={ index }>{ clientError.message }</li>
          ) ) }
        </ul>
      </div>
    ) }
  </>
);

ClientErrorsList.displayName = 'ClientErrorsList';

const ProtocolErrorsList = ( { errors }: { errors: ApolloError['protocolErrors'] } ) => (
  <>
    { errors.length > 0 && (
      <div className={ BEM.$( bem.el( 'suberror' ), bem.el( 'protocol-errors' ) ) }>
        <div className={ BEM.$( bem.el( 'suberror-heading' ) ) }>Protocol Errors:</div>
        <ul>
          { errors.map( ( protocolError, index ) => (
            <li key={ index }>{ protocolError.message }</li>
          ) ) }
        </ul>
      </div>
    ) }
  </>
);

ProtocolErrorsList.displayName = 'ProtocolErrorsList';

const ExtraInfo = ( { extraInfo }: { extraInfo: any } ) => (
  <>{ extraInfo && <div className={ BEM.$( bem.el( 'extra-info' ) ) }>Extra Info: { extraInfo }</div> }</>
);

ExtraInfo.displayName = 'ExtraInfo';

export const ErrorDisplayApolloError = ( props: { error: ApolloError, disableDevelopmentMode?: boolean } ) => {
  const { disableDevelopmentMode, error } = props;

  return (
    <div className={ BEM.$( bem ) }>
      <div className={ BEM.$( bem.el( 'message' ) ) }>{ error.message }</div>

      { disableDevelopmentMode !== true
        && environment.appMode === 'development'
        && (
          <div className={ BEM.$( bem.el( 'development' ) ) }>
            <div className={ BEM.$( bem.el( 'stack' ) ) }>{ error.stack }</div>

            <NetworkError error={ error.networkError } />
            <GraphQLErrorsList errors={ error.graphQLErrors } />
            <ClientErrorsList errors={ error.clientErrors } />
            <ProtocolErrorsList errors={ error.protocolErrors } />
            <ExtraInfo extraInfo={ error.extraInfo } />
          </div>
        )
      }
    </div>
  );
};

ErrorDisplayApolloError.displayName = 'ErrorDisplayApolloError';
